import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { m } from 'framer-motion';
import {
  FiCalendar, FiClock, FiPackage, FiPlus, FiMinus, FiTrash2,
  FiCheck, FiAlertCircle, FiArrowLeft, FiArrowRight, FiInfo, FiLoader, FiCreditCard
} from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useTheme } from '../../context/ThemeContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from 'react-i18next';
import { useMotionConfig } from '../../hooks/useReducedMotion';
import { STRIPE_CONFIG } from '../../config/stripe';
import { groupBookingItems } from '../../utils/groupBookingItems';
import ManageHeader from './ManageHeader';
import AddProductsSection from './AddProductsSection';
import AddExtrasSection from './AddExtrasSection';
import AddonsModal from '../ui/AddonsModal';
import InsuranceWarningModal from '../ui/InsuranceWarningModal';
import DeltaPaymentMethods from './DeltaPaymentMethods';
import { useDeltaPaymentReturn } from './deltaPayment';
import {
  toLocalDatetimeValue,
  formatDisplayDate,
  validateDateAgainstOpeningHours,
  validateDateAgainstClosingPeriods,
  recalculatePrices,
  findBestPricingMatch
} from './editFlowUtils';

const API_BASE = STRIPE_CONFIG.apiBaseUrl || '/api';

// bundle_components is JSONB; usually an array, guard against a JSON string.
function normalizeBundleComponents(raw) {
  let value = raw;
  if (typeof value === 'string') {
    try { value = JSON.parse(value); } catch { return null; }
  }
  return Array.isArray(value) && value.length > 0 ? value : null;
}

// Resolve a booking's items into bundles (a parent + its materialized component
// rows) and standalone items, so a bundle can be edited/removed atomically.
//
// A bundle parent is an item whose product type carries bundle_components (or is
// flagged is_bundle_parent). Its components are preferably the rows linked via
// parent_booking_item_id; for legacy bookings where that link was never set, we
// infer them by claiming the bundle's component product types (cheapest first,
// so the €0 materialized rows are taken before any genuine standalone item).
function resolveBundles(items) {
  const isParent = (i) =>
    i.is_bundle_parent === true || normalizeBundleComponents(i.product_types?.bundle_components) != null;
  const parents = items.filter(isParent);
  const claimed = new Set();

  const bundles = parents.map((parent) => {
    const linked = items.filter((i) => i.id !== parent.id && i.parent_booking_item_id === parent.id);
    let components = linked;
    if (components.length === 0) {
      const specs = normalizeBundleComponents(parent.product_types?.bundle_components) || [];
      const inferred = [];
      for (const spec of specs) {
        let need = (Number(spec.quantity) || 1) * (parent.quantity || 1);
        const candidates = items
          .filter((i) =>
            !isParent(i) &&
            !claimed.has(i.id) &&
            i.product_type_id === spec.product_type_id &&
            (spec.size == null || (i.size || null) === (spec.size || null))
          )
          .sort((a, b) => (Number(a.unit_price) || 0) - (Number(b.unit_price) || 0));
        for (const c of candidates) {
          if (need <= 0) break;
          inferred.push(c);
          claimed.add(c.id);
          need -= c.quantity || 1;
        }
      }
      components = inferred;
    } else {
      linked.forEach((c) => claimed.add(c.id));
    }
    return { parent, components };
  });

  const bundleItemIds = new Set();
  bundles.forEach((b) => {
    bundleItemIds.add(b.parent.id);
    b.components.forEach((c) => bundleItemIds.add(c.id));
  });
  const standaloneItems = items.filter((i) => !bundleItemIds.has(i.id));

  return { bundles, standaloneItems, bundleItemIds };
}

export default function EditFlow({ token, bookingData, refreshBooking }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const { formatCurrency } = useCurrency();
  const { getProductTranslation } = useLanguage();
  const { t } = useTranslation();
  const motion = useMotionConfig();

  const { booking, items, addons, merchant, location, closingPeriods } = bookingData;

  const [step, setStep] = useState('overview');
  const [editedItems, setEditedItems] = useState(
    items.map(i => ({ ...i, originalQuantity: i.quantity, originalUnitPrice: i.unit_price }))
  );
  const [editedAddons, setEditedAddons] = useState(
    groupBookingItems(addons).map(a => ({ ...a, originalQuantity: a.quantity, originalUnitPrice: a.unit_price }))
  );
  const [addedProducts, setAddedProducts] = useState([]);

  const merchantTz = merchant?.timezone || undefined;

  // Cancellation cutoff: after this moment the favorable refund tier is no
  // longer reachable. Pushing the start date *later* would reset the clock
  // and let the customer re-enter the refund window, so we cap the start
  // date at its current value once past cutoff. Earlier moves stay free —
  // they can only ever reduce hours_until_start, never increase it.
  // Single-tier policy today; if/when it becomes multi-tier, anchor on the
  // largest hours_before across tiers with refund_percentage > 0.
  const hoursBeforeStart = merchant?.cancellation_policy?.hours_before_start ?? 0;
  const cancellationCutoff = new Date(
    new Date(booking.start_date).getTime() - hoursBeforeStart * 3600000
  );
  const pastCancellationCutoff = hoursBeforeStart > 0 && new Date() >= cancellationCutoff;

  // Once the rental has started, end_date changes must go through the
  // extension flow (extension-specific pricing rules). Letting the edit
  // flow change end_date here would compute a different price for the
  // same conceptual extension using normal rules.
  const rentalStarted = new Date(booking.start_date) <= new Date();

  const [editedStartDate, setEditedStartDate] = useState(toLocalDatetimeValue(booking.start_date, merchantTz));
  const [editedEndDate, setEditedEndDate] = useState(toLocalDatetimeValue(booking.end_date, merchantTz));
  const originalStartDate = useRef(toLocalDatetimeValue(booking.start_date, merchantTz));
  const originalEndDate = useRef(toLocalDatetimeValue(booking.end_date, merchantTz));
  const [dateError, setDateError] = useState(null);
  const [priceAnchorError, setPriceAnchorError] = useState(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [availabilityOk, setAvailabilityOk] = useState(null);
  const availabilityTimer = useRef(null);

  const [recalculatingPrices, setRecalculatingPrices] = useState(false);
  const [pricesRecalculated, setPricesRecalculated] = useState(false);
  const priceTimer = useRef(null);

  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [delta, setDelta] = useState(0);
  const [savedCard, setSavedCard] = useState(null); // { brand, last4 } when a reusable card is on file

  const [showAddonsModal, setShowAddonsModal] = useState(false);
  const [addonsData, setAddonsData] = useState(null);
  const [newAddons, setNewAddons] = useState([]);
  const [isInsuranceWarningOpen, setIsInsuranceWarningOpen] = useState(false);
  const [pendingAddons, setPendingAddons] = useState(null);
  const addonsFetchedRef = useRef(false);
  const addonsModalShownRef = useRef(false);

  const isUnpaid = booking.payment_state !== 'paid' && booking.payment_state !== 'open_pay_later';
  const maxFreeEdits = merchant?.max_free_edits ?? null;
  const currentEditCount = booking.free_edit_count || 0;
  const isFreeEdit = isUnpaid || maxFreeEdits === null || currentEditCount < maxFreeEdits;

  const allItems = [...editedItems, ...addedProducts];

  const depositRules = merchant?.deposits_enabled ? (merchant?.deposit_rules || []) : null;

  const getDepositAmount = (categorySlug) => {
    if (!depositRules || depositRules.length === 0) return 0;
    const rule = depositRules.find(r => r.category_slug === categorySlug) ||
                 depositRules.find(r => r.category_slug === '*');
    return rule ? rule.amount : 0;
  };

  // Compute swap budget: deposit freed by removed items minus deposit used by added items
  const freedDeposit = editedItems
    .filter(i => i.quantity === 0 && i.originalQuantity > 0 && i.category_slug)
    .reduce((sum, i) => sum + getDepositAmount(i.category_slug) * i.originalQuantity, 0);
  const usedDeposit = addedProducts
    .filter(p => p.quantity > 0 && p.category_slug)
    .reduce((sum, p) => sum + getDepositAmount(p.category_slug) * p.quantity, 0);
  const depositSwapBudget = freedDeposit - usedDeposit;
  // Bundles are edited atomically; only standalone items get per-line controls.
  const { bundles, standaloneItems, bundleItemIds } = useMemo(() => resolveBundles(items), [items]);
  const groupedOriginalItems = groupBookingItems(standaloneItems);
  const datesChanged = editedStartDate !== originalStartDate.current || editedEndDate !== originalEndDate.current;

  const calculateNewTotal = useCallback(() => {
    const itemTotal = editedItems.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
    const addedTotal = addedProducts.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
    const addonTotal = editedAddons.reduce((sum, a) => sum + (a.unit_price * a.quantity), 0);
    const newAddonTotal = newAddons.reduce((sum, a) => sum + (parseFloat(a.price) * a.quantity), 0);
    return itemTotal + addedTotal + addonTotal + newAddonTotal;
  }, [editedItems, addedProducts, editedAddons, newAddons]);

  const newSubtotal = calculateNewTotal();
  const existingDiscount = parseFloat(booking.total_discount) || 0;
  // tax_rate is stored as a decimal (e.g. 0.09 = 9%, 0.20 = 20%) — do NOT divide by 100.
  const taxRate = parseFloat(booking.tax_rate) || 0;
  const taxIncluded = booking.tax_included;
  const currentTotal = parseFloat(booking.total_amount);
  const currentTax = parseFloat(booking.total_tax) || 0;

  // Calculate delta from actual changes: new items, removed items, and repriced items.
  // Don't re-derive total from scratch — booking.total_amount is the authoritative base
  // (it reflects extensions, discounts, etc. that item unit_prices don't capture).
  const addedValue = addedProducts.reduce((sum, p) => sum + (p.unit_price * p.quantity), 0)
    + newAddons.reduce((sum, a) => sum + (parseFloat(a.price) * a.quantity), 0);

  // Net change on existing lines = new line value − original line value. One
  // expression covers quantity increases, decreases, and per-unit reprices
  // (date changes). The earlier remove/reprice split missed quantity
  // increases on existing lines (notably bumping an addon already in the cart).
  const existingItemsDelta = editedItems
    .reduce((sum, i) => sum + (i.unit_price * i.quantity - i.originalUnitPrice * i.originalQuantity), 0);
  // Mirror the item formula so a per-unit addon reprice (from a date change)
  // flows into the delta, not just quantity changes. The earlier
  // `unit_price * (quantity - originalQuantity)` form zeroed out whenever the
  // quantity was unchanged, so an extended rental never charged for the addon's
  // longer-duration price.
  const existingAddonsDelta = editedAddons
    .reduce((sum, a) => sum + (a.unit_price * a.quantity - a.originalUnitPrice * a.originalQuantity), 0);

  // Item-level delta is expressed in the same unit as item unit prices —
  // gross in inclusive mode (EU/VAT), net in exclusive mode (US/sales tax).
  const itemsDelta = addedValue + existingItemsDelta + existingAddonsDelta;
  const taxDelta = taxIncluded
    ? (taxRate > 0 ? (itemsDelta * taxRate) / (1 + taxRate) : 0)  // tax portion embedded in the gross delta
    : itemsDelta * taxRate;                                        // tax added on top of the net delta
  const grossDelta = taxIncluded ? itemsDelta : itemsDelta + taxDelta;

  const priceDelta = Math.round(grossDelta * 100) / 100;
  const newTax = Math.round((currentTax + taxDelta) * 100) / 100;
  const newTotal = Math.round((currentTotal + grossDelta) * 100) / 100;

  const hasChanges = () => {
    const itemChanged = editedItems.some(i => i.quantity !== i.originalQuantity);
    const addonChanged = editedAddons.some(a => a.quantity !== a.originalQuantity);
    const productsAdded = addedProducts.some(p => p.quantity > 0);
    const newAddonsAdded = newAddons.length > 0;
    return itemChanged || addonChanged || datesChanged || productsAdded || newAddonsAdded;
  };

  // Remove a bundle as a unit: zero the parent AND its component rows, so the
  // free component bikes can't linger on the booking while the bundle price is
  // credited.
  const removeBundle = (bundle) => {
    const ids = new Set([bundle.parent.id, ...bundle.components.map(c => c.id)]);
    setEditedItems(prev => prev.map(i =>
      ids.has(i.id) ? { ...i, quantity: 0, total_price: 0 } : i
    ));
  };

  const updateGroupQuantity = (groupKey, change) => {
    setEditedItems(prev => {
      const updated = [...prev];
      const groupItems = updated.filter(i => `${i.product_type_id}_${i.size || ''}` === groupKey && !bundleItemIds.has(i.id));

      if (change > 0) {
        const template = groupItems.find(i => i.quantity > 0) || groupItems[0];
        if (template) {
          const zeroItem = groupItems.find(i => i.quantity === 0);
          if (zeroItem) {
            const idx = updated.indexOf(zeroItem);
            updated[idx] = { ...zeroItem, quantity: 1, total_price: zeroItem.unit_price };
          }
        }
      } else {
        const activeItems = groupItems.filter(i => i.quantity > 0);
        if (activeItems.length > 0) {
          const toDisable = activeItems[activeItems.length - 1];
          const idx = updated.indexOf(toDisable);
          updated[idx] = { ...toDisable, quantity: 0, total_price: 0 };
        }
      }
      return updated;
    });
  };

  const currentGroupQty = (groupKey) => {
    return editedItems
      .filter(i => `${i.product_type_id}_${i.size || ''}` === groupKey && !bundleItemIds.has(i.id) && i.quantity > 0)
      .length;
  };

  const removeGroup = (groupKey) => {
    setEditedItems(prev => prev.map(i =>
      `${i.product_type_id}_${i.size || ''}` === groupKey && !bundleItemIds.has(i.id)
        ? { ...i, quantity: 0, total_price: 0 }
        : i
    ));
  };

  const maxGroupQty = (groupKey) => {
    return editedItems
      .filter(i => `${i.product_type_id}_${i.size || ''}` === groupKey && !bundleItemIds.has(i.id))
      .length;
  };

  const originalGroupQty = (groupKey) => {
    return items
      .filter(i => `${i.product_type_id}_${i.size || ''}` === groupKey && !bundleItemIds.has(i.id))
      .length;
  };

  const getAddonMaxQuantity = (addon) => {
    const info = addonsData?.[addon.product_type_id];
    if (!info) return Infinity;
    if (info.inventory_type === 'unlimited') return Infinity;
    if (info.inventory_type === 'pool' && info.pool_quantity) return info.pool_quantity;
    return Infinity;
  };

  const updateAddonQuantity = (addonId, delta) => {
    setEditedAddons(prev => prev.map(a => {
      if (a.id !== addonId) return a;
      const max = getAddonMaxQuantity(a);
      const newQty = Math.min(max, Math.max(0, a.quantity + delta));
      return { ...a, quantity: newQty, total_price: a.unit_price * newQty };
    }));
  };

  const handleAddProduct = (product, size = null) => {
    const price = product.calculatedPrice?.price || 0;
    addonsModalShownRef.current = false; // Reset so addons modal re-appears for updated product set
    setAddedProducts(prev => {
      const existing = prev.find(p => p.product_type_id === product.id && (p.size || null) === (size || null));
      if (existing) {
        return prev.map(p =>
          p.product_type_id === product.id && (p.size || null) === (size || null)
            ? { ...p, quantity: p.quantity + 1, total_price: price * (p.quantity + 1) }
            : p
        );
      }
      return [...prev, {
        product_type_id: product.id,
        product_type: product.product_type || 'bike',
        product_name: product.product_name,
        picture_url: product.picture_url,
        translations: product.translations,
        size: size || null,
        quantity: 1,
        unit_price: price,
        total_price: price,
        inventory_item_id: null,
        isNew: true,
        duration_type: product.calculatedPrice?.duration_type || null,
        duration_value: product.calculatedPrice?.duration_value || null,
        duration_label: product.calculatedPrice?.duration_label || null,
        availableAddons: product.availableAddons || [],
        recommendedAddons: product.recommendedAddons || [],
        category_slug: product.category_slug || null
      }];
    });
  };

  const handleRemoveProduct = (productId, size = null) => {
    addonsModalShownRef.current = false; // Reset so addons modal re-appears for updated product set
    setAddedProducts(prev => {
      const existing = prev.find(p => p.product_type_id === productId && (p.size || null) === (size || null));
      if (!existing) return prev;
      if (existing.quantity <= 1) {
        return prev.filter(p => !(p.product_type_id === productId && (p.size || null) === (size || null)));
      }
      return prev.map(p =>
        p.product_type_id === productId && (p.size || null) === (size || null)
          ? { ...p, quantity: p.quantity - 1, total_price: p.unit_price * (p.quantity - 1) }
          : p
      );
    });
  };

  const handleAddStandaloneAddon = (addon) => {
    setNewAddons(prev => {
      const existing = prev.find(a => a.id === addon.id);
      if (existing) {
        return prev.map(a => a.id === addon.id ? { ...a, quantity: a.quantity + 1 } : a);
      }
      return [...prev, {
        id: addon.id,
        name: addon.product_name,
        product_name: addon.product_name,
        price: addon.price,
        quantity: 1,
        translations: addon.translations
      }];
    });
  };

  const handleRemoveStandaloneAddon = (addonId) => {
    setNewAddons(prev => {
      const existing = prev.find(a => a.id === addonId);
      if (!existing) return prev;
      if (existing.quantity <= 1) return prev.filter(a => a.id !== addonId);
      return prev.map(a => a.id === addonId ? { ...a, quantity: a.quantity - 1 } : a);
    });
  };

  // Fetch the saved card on file (if any) so an edit that adds cost can be
  // paid with one tap instead of fresh card entry.
  const savedCardFetchedRef = useRef(false);
  useEffect(() => {
    if (savedCardFetchedRef.current) return;
    savedCardFetchedRef.current = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/manage/saved-card`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) return;
        const data = await res.json();
        setSavedCard(data.savedCard || null);
      } catch {
        // no saved card → fresh entry
      }
    })();
  }, [token]);

  useEffect(() => {
    if (!datesChanged) {
      setDateError(null);
      setAvailabilityOk(null);
      setPricesRecalculated(false);
      return;
    }

    const start = new Date(editedStartDate);
    const end = new Date(editedEndDate);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      setDateError(null);
      setAvailabilityOk(null);
      return;
    }

    if (end <= start) {
      setDateError(t('manage.endBeforeStart', 'Return date must be after pick-up date.'));
      setAvailabilityOk(false);
      return;
    }

    const formatHoursError = (err, label) => {
      if (err.type === 'closed') return `${label}: ${t('manage.closedOnDay', 'Location is closed on this day')}`;
      return `${label}: ${t('manage.outsideOpeningHours', 'Outside opening hours ({{open}} – {{close}})', { open: err.open, close: err.close })}`;
    };

    const startHoursError = validateDateAgainstOpeningHours(editedStartDate, location?.opening_hours, merchantTz);
    if (startHoursError) {
      setDateError(formatHoursError(startHoursError, t('manage.pickUp', 'Pick-up')));
      setAvailabilityOk(false);
      return;
    }

    const endHoursError = validateDateAgainstOpeningHours(editedEndDate, location?.opening_hours, merchantTz);
    if (endHoursError) {
      setDateError(formatHoursError(endHoursError, t('manage.returnBy', 'Return by')));
      setAvailabilityOk(false);
      return;
    }

    const closingError = validateDateAgainstClosingPeriods(editedStartDate, editedEndDate, closingPeriods, merchantTz);
    if (closingError) {
      const reason = closingError.reason ? ` (${closingError.reason})` : '';
      setDateError(t('manage.closingPeriodOverlap', 'Rental period overlaps with a closing period') + reason);
      setAvailabilityOk(false);
      return;
    }

    setDateError(null);
    setAvailabilityOk(null);
    setCheckingAvailability(true);
    setPricesRecalculated(false);

    if (availabilityTimer.current) clearTimeout(availabilityTimer.current);

    availabilityTimer.current = setTimeout(async () => {
      try {
        const allActiveItems = [
          ...editedItems.filter(i => i.quantity > 0),
          ...addedProducts.filter(p => p.quantity > 0)
        ];
        // Group by product_type_id + size for accurate per-size availability checks
        const groupedItems = {};
        allActiveItems.forEach(i => {
          const key = `${i.product_type_id}_${i.size || ''}`;
          if (!groupedItems[key]) {
            groupedItems[key] = { productTypeId: i.product_type_id, size: i.size || null, needed: 0 };
          }
          groupedItems[key].needed += i.quantity;
        });

        let allAvailable = true;
        for (const group of Object.values(groupedItems)) {
          const params = new URLSearchParams({
            locationId: booking.location_id,
            startDate: start.toISOString(),
            endDate: end.toISOString(),
            productTypeId: group.productTypeId,
            excludeBookingId: booking.id
          });
          if (group.size) params.set('size', group.size);
          const res = await fetch(`${API_BASE}/availability?${params}`);
          if (!res.ok) { allAvailable = false; break; }
          const data = await res.json();
          const avail = (data.availability || []).reduce((sum, entry) => sum + (entry.available_count || 0), 0);
          if (avail < group.needed) { allAvailable = false; break; }
        }

        setAvailabilityOk(allAvailable);
        if (!allAvailable) {
          setDateError(t('manage.noAvailability', 'Not enough availability for the selected dates.'));
        }
      } catch {
        setAvailabilityOk(null);
      } finally {
        setCheckingAvailability(false);
      }
    }, 600);

    return () => {
      if (availabilityTimer.current) clearTimeout(availabilityTimer.current);
    };
  }, [editedStartDate, editedEndDate]);

  // Pristine duration_type per product, captured from the booking as loaded.
  // editedItems' duration_type is overwritten on every reprice, so the original
  // rate type has to come from the untouched `items` / `addons` props. Used only
  // to break ties between rules covering identical hours.
  const originalDurationTypes = useMemo(() => {
    const byProduct = new Map();
    [...(items || []), ...(addons || [])].forEach((i) => {
      if (i?.product_type_id && i.duration_type) byProduct.set(i.product_type_id, i.duration_type);
    });
    return byProduct;
  }, [items, addons]);

  // Re-anchor guard.
  //
  // Re-prices the ORIGINAL, unedited window through the same path the edit screen
  // uses. If that doesn't reproduce the unit prices the customer actually paid,
  // then every delta this screen computes is untrustworthy, so we refuse to quote
  // one rather than charging a difference we can't justify. This is the check
  // that would have caught the +218,00 € on BK-2026-006126 before a customer saw
  // it.
  //
  // Compares per-item unit_price rather than booking.total_amount: the total also
  // carries tax, discounts and fees that the pricing engine never sees, so it
  // can't be reconstructed from pricing rules alone.
  //
  // Runs once per booking, not per edit — it's a property of the booking.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const originals = [...(items || []), ...(addons || [])]
        .filter((i) => i?.product_type_id && i.quantity > 0 && Number.isFinite(parseFloat(i.unit_price)));
      if (originals.length === 0) return;

      try {
        const map = await recalculatePrices(originals, booking.location_id, booking.start_date);
        if (cancelled) return;

        const mismatches = [];
        for (const item of originals) {
          const options = map.get(item.product_type_id);
          // Rules deleted since the booking was made can't be verified. That's a
          // gap, not a mismatch — don't lock a customer out of editing over it.
          if (!options || options.length === 0) continue;

          const match = findBestPricingMatch(
            options, booking.start_date, booking.end_date, item.duration_type || null
          );
          if (!match) continue;

          const repriced = parseFloat(match.price);
          const paid = parseFloat(item.unit_price);
          if (!Number.isFinite(repriced)) continue;
          if (Math.abs(repriced - paid) > 0.005) {
            mismatches.push({
              product_type_id: item.product_type_id,
              item: item.item_name,
              paid,
              repriced,
              rule: match.duration_label
            });
          }
        }

        if (mismatches.length > 0) {
          console.error(
            '[EDIT-FLOW] Re-pricing the original window does not reproduce what was paid. ' +
            'Blocking edits — any delta shown would be wrong:', mismatches
          );
          setPriceAnchorError(t(
            'manage.priceAnchorMismatch',
            'We can\'t reliably reprice this booking right now, so changes are temporarily unavailable. Please contact us and we\'ll make the change for you.'
          ));
        }
      } catch {
        // Network/pricing outage: leave editing enabled rather than blocking on
        // a transient failure. The delta itself is still guarded by the
        // durationExceeded check and the server-side re-validation on save.
      }
    })();

    return () => { cancelled = true; };
  }, [booking.id]);

  useEffect(() => {
    if (!datesChanged) return;

    const start = new Date(editedStartDate);
    const end = new Date(editedEndDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime()) || end <= start) return;

    setRecalculatingPrices(true);
    if (priceTimer.current) clearTimeout(priceTimer.current);

    priceTimer.current = setTimeout(async () => {
      try {
        const priceMap = await recalculatePrices(editedItems, booking.location_id, editedStartDate);
        const addedPriceMap = addedProducts.length > 0
          ? await recalculatePrices(addedProducts, booking.location_id, editedStartDate)
          : null;

        // Addons are duration-priced too (per-day insurance, etc.), so an
        // extended rental must reprice them exactly like products. Flat addons
        // are modelled as pricing rules that resolve to the same price at every
        // duration, so this stays a no-op for them. newAddons key their product
        // type on `id` (not `product_type_id`), so normalize before pricing.
        const addonPriceMap = editedAddons.length > 0
          ? await recalculatePrices(editedAddons, booking.location_id, editedStartDate)
          : null;
        const newAddonItems = newAddons.map(a => ({ ...a, product_type_id: a.id }));
        const newAddonPriceMap = newAddonItems.length > 0
          ? await recalculatePrices(newAddonItems, booking.location_id, editedStartDate)
          : null;

        // Detect duration overrun before writing state. Doing it inside the
        // setEditedItems / setAddedProducts updaters would race: React defers
        // the updater functions, so the durationExceeded flag would still be
        // false when the synchronous if-check below runs.
        //
        // Without this guard, the old findBestPricingMatch fallback would
        // silently price e.g. a 2-month rental at the 4-hour rate.
        const overflows = (items, map) => items.some(item => {
          if (item.quantity === 0) return false;
          const options = map?.get(item.product_type_id);
          if (!options || options.length === 0) return false;
          return findBestPricingMatch(options, editedStartDate, editedEndDate) === null;
        });
        const durationExceeded =
          overflows(editedItems, priceMap) ||
          (addedPriceMap !== null && overflows(addedProducts, addedPriceMap)) ||
          (addonPriceMap !== null && overflows(editedAddons, addonPriceMap)) ||
          (newAddonPriceMap !== null && overflows(newAddonItems, newAddonPriceMap));

        if (durationExceeded) {
          setDateError(t('manage.durationExceedsPricing', 'Selected rental duration exceeds the longest available rental option.'));
          setAvailabilityOk(false);
          setPricesRecalculated(false);
          return;
        }

        setEditedItems(prev => prev.map(item => {
          if (item.quantity === 0) return item;
          const options = priceMap.get(item.product_type_id);
          if (!options || options.length === 0) return item;
          const match = findBestPricingMatch(
            options, editedStartDate, editedEndDate,
            originalDurationTypes.get(item.product_type_id) || null
          );
          if (!match) return item;
          const newPrice = parseFloat(match.price);
          if (isNaN(newPrice) || newPrice <= 0) return item;
          return {
            ...item,
            unit_price: newPrice,
            total_price: newPrice * item.quantity,
            duration_type: match.duration_type,
            duration_value: match.duration_value,
            duration_label: match.duration_label
          };
        }));

        if (addedPriceMap !== null) {
          setAddedProducts(prev => prev.map(item => {
            if (item.quantity === 0) return item;
            const options = addedPriceMap.get(item.product_type_id);
            if (!options || options.length === 0) return item;
            const match = findBestPricingMatch(options, editedStartDate, editedEndDate);
            if (!match) return item;
            const newPrice = parseFloat(match.price);
            if (isNaN(newPrice) || newPrice <= 0) return item;
            return {
              ...item,
              unit_price: newPrice,
              total_price: newPrice * item.quantity,
              duration_type: match.duration_type,
              duration_value: match.duration_value,
              duration_label: match.duration_label
            };
          }));
        }

        if (addonPriceMap !== null) {
          setEditedAddons(prev => prev.map(addon => {
            if (addon.quantity === 0) return addon;
            const options = addonPriceMap.get(addon.product_type_id);
            if (!options || options.length === 0) return addon;
            const match = findBestPricingMatch(
              options, editedStartDate, editedEndDate,
              originalDurationTypes.get(addon.product_type_id) || null
            );
            if (!match) return addon;
            const newPrice = parseFloat(match.price);
            if (isNaN(newPrice) || newPrice <= 0) return addon;
            return {
              ...addon,
              unit_price: newPrice,
              total_price: newPrice * addon.quantity
            };
          }));
        }

        if (newAddonPriceMap !== null) {
          setNewAddons(prev => prev.map(addon => {
            if (addon.quantity === 0) return addon;
            const options = newAddonPriceMap.get(addon.id);
            if (!options || options.length === 0) return addon;
            const match = findBestPricingMatch(options, editedStartDate, editedEndDate);
            if (!match) return addon;
            const newPrice = parseFloat(match.price);
            if (isNaN(newPrice) || newPrice <= 0) return addon;
            // newAddons carry their amount on `price` (consumed as parseFloat elsewhere).
            return { ...addon, price: newPrice };
          }));
        }

        setPricesRecalculated(true);
      } catch {
        // keep existing prices on error
      } finally {
        setRecalculatingPrices(false);
      }
    }, 800);

    return () => {
      if (priceTimer.current) clearTimeout(priceTimer.current);
    };
  }, [editedStartDate, editedEndDate]);

  // Fetch addons data for the location (needed for addons modal and insurance detection)
  useEffect(() => {
    if (addonsFetchedRef.current) return;
    // Fetch if booking has existing addons OR newly added products have available addons
    const hasExistingAddons = editedAddons.length > 0;
    const hasNewProductsWithAddons = addedProducts.some(p => p.availableAddons?.length > 0);
    if (!hasExistingAddons && !hasNewProductsWithAddons) return;

    addonsFetchedRef.current = true;
    const fetchAddons = async () => {
      try {
        const res = await fetch(`${API_BASE}/addons?locationId=${location.id}`);
        if (!res.ok) return;
        const data = await res.json();
        // Convert array to object keyed by ID (as AddonsModal expects)
        const addonsMap = {};
        (data.addons || []).forEach(addon => {
          addonsMap[addon.id] = {
            ...addon,
            name: addon.product_name,
            price: addon.pricing_rules?.[0]?.price || 0,
            _pricingRules: addon.pricing_rules || []
          };
        });
        setAddonsData(addonsMap);
      } catch {
        // Addons are optional; silently continue without them
      }
    };
    fetchAddons();
  }, [addedProducts, editedAddons.length, location.id]);

  const handleReview = () => {
    if (!hasChanges()) {
      setError(t('manage.noChanges', 'No changes have been made.'));
      return;
    }
    if (pastCancellationCutoff && editedStartDate > originalStartDate.current) {
      setError(t('manage.startDateCannotMoveLater', 'The pick-up date can only be moved earlier — the cancellation deadline has passed.'));
      return;
    }
    if (rentalStarted && editedEndDate !== originalEndDate.current) {
      setError(t('manage.endDateEditAfterStart', 'To extend an in-progress rental, use Extend Booking.'));
      return;
    }
    if (getTotalBikesCount() === 0) {
      setError(t('manage.cannotRemoveAllItems', 'A booking must keep at least one item. Add a replacement item or use Cancel Booking to remove the rental entirely.'));
      return;
    }
    // Re-anchor guard: if re-pricing the original window doesn't reproduce what
    // the customer paid, no delta from this screen can be trusted.
    if (priceAnchorError) {
      setError(priceAnchorError);
      return;
    }
    if (datesChanged && (!availabilityOk || !pricesRecalculated)) {
      setError(dateError || t('manage.checkAvailability', 'Please wait for availability check to complete.'));
      return;
    }
    setError(null);

    // Check if newly added products have available addons (only show modal once)
    const hasNewProductsWithAddons = addedProducts.some(p => p.availableAddons?.length > 0);
    if (hasNewProductsWithAddons && addonsData && Object.keys(addonsData).length > 0 && newAddons.length === 0 && !addonsModalShownRef.current) {
      setShowAddonsModal(true);
      return;
    }

    // Check insurance count matches total bikes (across existing + new addons)
    if (checkInsuranceMismatch()) {
      setPendingAddons(newAddons);
      setIsInsuranceWarningOpen(true);
      return;
    }

    setStep('review');
  };

  const getTotalBikesCount = () => {
    const isMainProduct = (item) => item.product_type !== 'addon';
    return editedItems.filter(isMainProduct).reduce((total, item) => total + item.quantity, 0)
      + addedProducts.filter(isMainProduct).reduce((total, p) => total + p.quantity, 0);
  };

  const isInsuranceAddon = (addon) => {
    // For new addons from the modal: check category directly
    const category = (addon.category || '').toLowerCase();
    if (category === 'insurance') return true;

    // For existing booking addons: cross-reference with addonsData by product_type_id
    if (addonsData && addon.product_type_id) {
      const addonInfo = addonsData[addon.product_type_id];
      if (addonInfo && (addonInfo.category || '').toLowerCase() === 'insurance') return true;
    }

    // Fallback: name matching
    const name = (addon.name || addon.item_name || addon.product_types?.product_name || '').toLowerCase();
    return name.includes('insurance') || name.includes('versicherung');
  };

  const getTotalInsuranceCount = (addonsList) => {
    // Count insurance from existing (edited) addons
    const existingInsurance = editedAddons
      .filter(a => isInsuranceAddon(a))
      .reduce((sum, a) => sum + a.quantity, 0);
    // Count insurance from newly selected addons
    const newInsurance = (addonsList || newAddons)
      .filter(a => isInsuranceAddon(a))
      .reduce((sum, a) => sum + a.quantity, 0);
    return existingInsurance + newInsurance;
  };

  const checkInsuranceMismatch = (addonsList) => {
    const totalBikes = getTotalBikesCount();
    const totalInsurance = getTotalInsuranceCount(addonsList);
    return totalInsurance > 0 && totalInsurance !== totalBikes;
  };

  const handleAddonsConfirm = (selectedAddonsList) => {
    setShowAddonsModal(false);
    addonsModalShownRef.current = true;

    if (checkInsuranceMismatch(selectedAddonsList)) {
      setPendingAddons(selectedAddonsList);
      setTimeout(() => setIsInsuranceWarningOpen(true), 100);
    } else {
      setNewAddons(selectedAddonsList);
      setStep('review');
    }
  };

  const handleInsuranceConfirm = () => {
    const totalBikes = getTotalBikesCount();
    // Determine how much insurance lives in existing vs new addons
    const existingInsuranceCount = editedAddons
      .filter(a => isInsuranceAddon(a))
      .reduce((sum, a) => sum + a.quantity, 0);

    // New addons that are insurance
    const pendingOrNewAddons = pendingAddons || newAddons;
    const newInsuranceInAddons = pendingOrNewAddons.filter(a => isInsuranceAddon(a));
    const hasNewInsuranceAddon = newInsuranceInAddons.length > 0;

    if (existingInsuranceCount > 0 && !hasNewInsuranceAddon) {
      // All insurance adjustment goes to existing addons
      setEditedAddons(prev => prev.map(a =>
        isInsuranceAddon(a) ? { ...a, quantity: totalBikes } : a
      ));
      setNewAddons(pendingOrNewAddons);
    } else if (hasNewInsuranceAddon) {
      // Distribute: keep existing as-is, adjust new to cover the rest
      const newInsuranceTarget = Math.max(0, totalBikes - existingInsuranceCount);
      const adjustedNewAddons = pendingOrNewAddons.map((addon) =>
        isInsuranceAddon(addon) ? { ...addon, quantity: newInsuranceTarget } : addon
      );
      setNewAddons(adjustedNewAddons.filter(a => !isInsuranceAddon(a) || a.quantity > 0));
    } else {
      // No insurance anywhere — nothing to adjust
      setNewAddons(pendingOrNewAddons);
    }

    setIsInsuranceWarningOpen(false);
    setPendingAddons(null);
    setStep('review');
  };

  const handleInsuranceDecline = () => {
    // Remove insurance from new addons
    const addonsWithoutInsurance = (pendingAddons || newAddons).filter(addon => !isInsuranceAddon(addon));
    // Zero out existing insurance addons
    setEditedAddons(prev => prev.map(a =>
      isInsuranceAddon(a) ? { ...a, quantity: 0 } : a
    ));
    setNewAddons(addonsWithoutInsurance);
    setIsInsuranceWarningOpen(false);
    setPendingAddons(null);
    setStep('review');
  };

  const buildChangesPayload = () => {
    const changedItemsList = [
      ...editedItems.filter(i => i.quantity > 0).map(i => ({
        productTypeId: i.product_type_id,
        inventoryItemId: i.inventory_item_id,
        itemName: i.item_name || i.product_types?.product_name,
        productType: i.product_type || 'bike',
        quantity: i.quantity,
        unitPrice: i.unit_price,
        totalPrice: i.unit_price * i.quantity,
        size: i.size,
        durationHours: i.duration_hours,
        durationType: i.duration_type,
        durationValue: i.duration_value,
        durationLabel: i.duration_label,
        // Existing rows: a bundle parent here already has its components
        // materialized as sibling rows, so the availability pre-check must NOT
        // re-expand it (see buildEditLeafDemands).
        isNew: false
      })),
      ...addedProducts.filter(p => p.quantity > 0).map(p => ({
        productTypeId: p.product_type_id,
        inventoryItemId: null,
        itemName: p.product_name,
        productType: p.product_type || 'bike',
        quantity: p.quantity,
        unitPrice: p.unit_price,
        totalPrice: p.unit_price * p.quantity,
        size: p.size,
        durationHours: p.duration_hours,
        durationType: p.duration_type,
        durationValue: p.duration_value,
        durationLabel: p.duration_label,
        // Newly added: a bundle added here has NO materialized component rows,
        // so the pre-check expands it into its components.
        isNew: true
      }))
    ];

    const changedAddonsList = [
      ...editedAddons
        .filter(a => a.quantity > 0)
        .map(a => ({
          addonId: a.product_type_id,
          name: a.item_name || a.product_types?.product_name,
          quantity: a.quantity,
          unitPrice: a.unit_price,
          totalPrice: a.unit_price * a.quantity
        })),
      ...newAddons
        .filter(a => a.quantity > 0)
        .map(a => ({
          addonId: a.id,
          name: a.name || a.product_name,
          quantity: a.quantity,
          unitPrice: parseFloat(a.price),
          totalPrice: parseFloat(a.price) * a.quantity
        }))
    ];

    const changes = {
      items: changedItemsList,
      addons: changedAddonsList,
      totalAmount: newTotal,
      subtotal: newSubtotal,
      totalDiscount: existingDiscount,
      totalTax: newTax
    };

    if (datesChanged) {
      changes.startDate = new Date(editedStartDate).toISOString();
      changes.endDate = new Date(editedEndDate).toISOString();
    }

    return changes;
  };

  // Confirm the Stripe payment server-side, then write the edit. Shared by the
  // inline success path (onPaid) and the redirect-return path. update-booking
  // fires the Make.com notification, so confirm-delta-payment is not asked to.
  const finalizeEdit = async (paymentIntentId, ctx = {}, { skipConfirm = false } = {}) => {
    if (!skipConfirm && paymentIntentId) {
      try {
        await fetch(`${API_BASE}/manage/confirm-delta-payment`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ paymentIntentId })
        });
      } catch (err) {
        console.warn('[EDIT] Failed to confirm payment server-side:', err.message);
      }
    }
    const ok = await executeUpdate(paymentIntentId, ctx.changes);
    if (!ok) { setFinalizing(false); setStep('overview'); }
    return ok;
  };

  // Finalize an edit paid via an off-site redirect (MobilePay, Pay-by-Bank).
  useDeltaPaymentReturn({
    onFinalize: finalizeEdit,
    onProcessing: () => setFinalizing(true),
    onError: (msg) => { setError(msg); setFinalizing(false); setStep('overview'); },
  });

  const handleConfirm = async () => {
    setSaving(true);
    setError(null);

    try {
      if (priceDelta <= 0 && maxFreeEdits !== null && !isFreeEdit) {
        setSaving(false);
        setStep('overview');
        return;
      }

      // Unpaid bookings: save changes, then redirect to PayFlow for full payment
      if (booking.payment_state !== 'paid' && booking.payment_state !== 'open_pay_later') {
        await executeUpdate();
        return;
      }

      if (priceDelta > 0 && booking.payment_state !== 'open_pay_later') {
        setDelta(priceDelta);
        setStep('payment');
        setSaving(false);
        return;
      }

      await executeUpdate();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const executeUpdate = async (paymentIntentId = null, changes = null) => {
    setSaving(true);
    try {
      const payload = changes || buildChangesPayload();

      const res = await fetch(`${API_BASE}/manage/update-booking`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          changes: payload,
          // Don't send updatedAt after a paid flow — the webhook will have
          // changed it between page load and now, causing a false 409 conflict.
          updatedAt: paymentIntentId ? undefined : booking.updated_at
        })
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        // Prefer a localized message keyed off the server's error code; fall
        // back to the server text, then a generic message.
        if (err.code === 'INVENTORY_CONFLICT') {
          throw new Error(t('manage.dateConflictError',
            'One or more of the selected items are not available for the new dates. Please choose different dates or items.'));
        }
        if (err.code === 'START_DATE_PAST_CUTOFF') {
          throw new Error(t('manage.startDateCannotMoveLater',
            'The pick-up date can only be moved earlier — the cancellation deadline has passed.'));
        }
        if (err.code === 'END_DATE_AFTER_START') {
          throw new Error(t('manage.endDateEditAfterStart',
            'To extend an in-progress rental, use Extend Booking.'));
        }
        throw new Error(err.error || t('manage.unexpectedErrorBody', 'An unexpected error occurred. Please try again.'));
      }

      setFinalizing(false);
      setStep('success');
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleDone = async () => {
    await refreshBooking();
    navigate(`/b/${token}`);
  };

  const handleGoToPayment = async () => {
    await refreshBooking();
    navigate(`/b/${token}/pay?skipAddons=1`);
  };

  // Redirect-based payment (MobilePay, Pay-by-Bank) is being finalized on return.
  if (finalizing) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ManageHeader merchant={merchant} token={token} />
        <main id="main-content" className="max-w-xl mx-auto px-4 py-12 flex justify-center">
          <div role="status" className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin" />
            <span className="text-sm text-gray-600">{t('manage.processingPayment', 'Processing payment...')}</span>
          </div>
        </main>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gray-50">
        <ManageHeader merchant={merchant} token={token} />
        <main id="main-content" className="max-w-xl mx-auto px-4 py-12 text-center">
          <m.div {...motion.scaleIn}>
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ backgroundColor: `${theme.colors.primary.base}15` }}
            >
              <SafeIcon icon={FiCheck} className="w-10 h-10" style={{ color: theme.colors.primary.base }} />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {t('manage.editSuccess', 'Booking Updated!')}
            </h1>
            <p className="text-gray-500 mb-8">
              {isUnpaid
                ? t('manage.editSuccessPayNow', 'Your changes have been saved. Please complete payment.')
                : t('manage.editSuccessDesc', 'Your booking has been successfully updated.')}
            </p>
            <button
              onClick={isUnpaid ? handleGoToPayment : handleDone}
              className="px-8 py-3 rounded-xl font-semibold text-white"
              style={{ backgroundColor: theme.colors.primary.base }}
            >
              {isUnpaid ? t('manage.proceedToPayment', 'Proceed to Payment') : t('manage.backToBooking', 'Back to Booking')}
            </button>
          </m.div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ManageHeader merchant={merchant} token={token} showBack />

      <main id="main-content" className="max-w-xl mx-auto px-4 py-6 space-y-6">
        <m.div {...motion.fadeInUp}>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">
            {t('manage.editBooking', 'Edit Booking')}
          </h1>
          <p className="text-gray-500 text-sm">
            {t('manage.editDescFull', 'Adjust dates, quantities, and add-ons.')}
          </p>
        </m.div>

        {step === 'overview' && !isUnpaid && maxFreeEdits !== null && (isFreeEdit || (hasChanges() && !isFreeEdit && priceDelta <= 0)) && (
          <m.div {...motion.fadeInUp}
            className="flex items-start gap-2 p-3 rounded-xl text-sm"
            style={{
              backgroundColor: isFreeEdit ? `${theme.colors.primary.base}10` : '#FEF2F2',
              color: isFreeEdit ? theme.colors.primary.base : '#B91C1C'
            }}
          >
            <SafeIcon icon={FiInfo} className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>
              {isFreeEdit
                ? t('manage.freeEditsRemaining', 'You have {{count}} free change(s) remaining.', { count: maxFreeEdits - currentEditCount })
                : t('manage.noFreeEdits', 'Free changes used. Please contact us for further changes. Additional charges may apply.')}
            </span>
          </m.div>
        )}

        {/* Re-anchor guard failed: explain why Review Changes is disabled rather
            than leaving the customer with a dead button. */}
        {priceAnchorError && (
          <div role="alert" className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-amber-800 text-sm">{priceAnchorError}</p>
          </div>
        )}

        {error && (
          <div role="alert" className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {step === 'overview' && (
          <>
            <m.div {...motion.fadeInUp} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <SafeIcon icon={FiCalendar} className="w-5 h-5" style={{ color: theme.colors.primary.base }} />
                {t('manage.rentalPeriod', 'Rental Period')}
              </h2>
              <div className="space-y-4">
                <div>
                  <label htmlFor="edit-pickup-date" className="block text-xs text-gray-500 uppercase tracking-wide mb-1.5">
                    {t('manage.pickUp', 'Pick-up')}
                  </label>
                  <input
                    id="edit-pickup-date"
                    type="datetime-local"
                    value={editedStartDate}
                    onChange={(e) => setEditedStartDate(e.target.value)}
                    disabled={new Date(booking.start_date) < new Date()}
                    max={pastCancellationCutoff ? originalStartDate.current : undefined}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-shadow disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                    style={{ focusRingColor: theme.colors.primary.base }}
                  />
                  {pastCancellationCutoff && new Date(booking.start_date) >= new Date() && (
                    <p className="mt-1.5 text-xs text-gray-500">
                      {t('manage.startDateCannotMoveLater', 'The pick-up date can only be moved earlier — the cancellation deadline has passed.')}
                    </p>
                  )}
                </div>
                <div>
                  <label htmlFor="edit-return-date" className="block text-xs text-gray-500 uppercase tracking-wide mb-1.5">
                    {t('manage.returnBy', 'Return by')}
                  </label>
                  <input
                    id="edit-return-date"
                    type="datetime-local"
                    value={editedEndDate}
                    onChange={(e) => setEditedEndDate(e.target.value)}
                    min={editedStartDate}
                    disabled={rentalStarted}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-shadow disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                    style={{ focusRingColor: theme.colors.primary.base }}
                  />
                  {rentalStarted && (
                    <p className="mt-1.5 text-xs text-gray-500">
                      {t('manage.endDateEditAfterStart', 'To extend an in-progress rental, use Extend Booking.')}
                    </p>
                  )}
                </div>
                {datesChanged && (
                  <div role="status" aria-live="polite" className="space-y-1">
                    <div className="flex items-center gap-2 text-sm">
                      {checkingAvailability && (
                        <>
                          <SafeIcon icon={FiLoader} className="w-4 h-4 text-gray-400 animate-spin" />
                          <span className="text-gray-500">{t('manage.checkingAvailability', 'Checking availability...')}</span>
                        </>
                      )}
                      {!checkingAvailability && availabilityOk === true && (
                        <>
                          <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-500" />
                          <span className="text-green-600">{t('manage.datesAvailable', 'Dates available')}</span>
                        </>
                      )}
                      {!checkingAvailability && dateError && (
                        <>
                          <SafeIcon icon={FiAlertCircle} className="w-4 h-4 text-red-500" />
                          <span className="text-red-600">{dateError}</span>
                        </>
                      )}
                    </div>
                    {recalculatingPrices && (
                      <div className="flex items-center gap-2 text-sm">
                        <SafeIcon icon={FiLoader} className="w-4 h-4 text-gray-400 animate-spin" />
                        <span className="text-gray-500">{t('manage.recalculatingPrices', 'Recalculating prices...')}</span>
                      </div>
                    )}
                    {!recalculatingPrices && pricesRecalculated && (
                      <div className="flex items-center gap-2 text-sm">
                        <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-500" />
                        <span className="text-green-600">{t('manage.pricesUpdated', 'Prices updated for new dates')}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </m.div>

            <m.div {...motion.fadeInUp} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <SafeIcon icon={FiPackage} className="w-5 h-5" style={{ color: theme.colors.primary.base }} />
                {t('manage.items', 'Items')}
              </h2>
              <div className="space-y-3">
                {bundles.map((bundle) => {
                  const parentEdited = editedItems.find(i => i.id === bundle.parent.id);
                  const isRemoved = !parentEdited || parentEdited.quantity === 0;
                  const pt = bundle.parent.product_types;
                  const bundleName = getProductTranslation(
                    pt?.product_name, pt?.product_type_translations, 'product_name'
                  );
                  const parentQty = parentEdited?.quantity ?? bundle.parent.quantity ?? 1;
                  const bundlePrice = (Number(bundle.parent.unit_price) || 0) * parentQty;
                  return (
                    <div key={`bundle-${bundle.parent.id}`} className={`p-3 bg-gray-50 rounded-xl ${isRemoved ? 'opacity-40' : ''}`}>
                      <div className="flex items-center gap-3">
                        {pt?.picture_url && (
                          <img src={pt.picture_url} alt={bundleName} className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className={`font-medium text-gray-900 truncate text-sm ${isRemoved ? 'line-through' : ''}`}>{bundleName}</p>
                            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700">
                              {t('manage.bundle', 'BUNDLE')}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500">
                            {isRemoved
                              ? t('manage.removed', 'Removed')
                              : `${parentQty > 1 ? `${parentQty}x ` : ''}${formatCurrency(bundlePrice)}`}
                          </p>
                        </div>
                        {!isRemoved && (
                          <button
                            onClick={() => removeBundle(bundle)}
                            className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-red-50 transition-colors"
                            aria-label={`${t('manage.remove', 'Remove')} ${bundleName}`}
                          >
                            <SafeIcon icon={FiTrash2} className="w-4 h-4 text-red-400" />
                          </button>
                        )}
                      </div>
                      {bundle.components.length > 0 && (
                        <ul className="mt-2 pl-3 border-l-2 border-gray-200 space-y-0.5">
                          {bundle.components.map((c) => {
                            const cpt = c.product_types;
                            const cName = getProductTranslation(
                              cpt?.product_name, cpt?.product_type_translations, 'product_name'
                            );
                            return (
                              <li key={`bc-${c.id}`} className={`text-xs text-gray-500 ${isRemoved ? 'line-through' : ''}`}>
                                {(c.quantity > 1 ? `${c.quantity}x ` : '') + cName}{c.size ? ` · ${c.size}` : ''}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  );
                })}

                {groupedOriginalItems.map((group) => {
                  const pt = group.product_types;
                  const productName = getProductTranslation(
                    pt?.product_name, pt?.product_type_translations, 'product_name'
                  );
                  const qty = currentGroupQty(group.groupKey);
                  const currentItem = editedItems.find(i =>
                    `${i.product_type_id}_${i.size || ''}` === group.groupKey && i.quantity > 0
                  );
                  const displayPrice = currentItem?.unit_price ?? group.unit_price;
                  const isRemoved = qty === 0;

                  return (
                    <div key={group.groupKey} className={`flex items-center gap-3 p-3 bg-gray-50 rounded-xl ${isRemoved ? 'opacity-40' : ''}`}>
                      {pt?.picture_url && (
                        <img src={pt.picture_url} alt={productName} className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium text-gray-900 truncate text-sm ${isRemoved ? 'line-through' : ''}`}>{productName}</p>
                        <p className="text-xs text-gray-500">
                          {group.size && `${group.size} · `}
                          {isRemoved
                            ? t('manage.removed', 'Removed')
                            : `${qty > 1 ? `${qty}x ` : ''}${formatCurrency(displayPrice * qty)}`}
                        </p>
                      </div>
                      {!isRemoved && (
                        <button
                          onClick={() => removeGroup(group.groupKey)}
                          className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-red-50 transition-colors"
                          aria-label={`${t('manage.remove', 'Remove')} ${productName}`}
                        >
                          <SafeIcon icon={FiTrash2} className="w-4 h-4 text-red-400" />
                        </button>
                      )}
                    </div>
                  );
                })}

                {addedProducts.filter(p => p.quantity > 0).map((product) => {
                  const productName = getProductTranslation(
                    product.product_name, product.translations, 'product_name'
                  );
                  const addedKey = `new-${product.product_type_id}_${product.size || ''}`;
                  return (
                    <div key={addedKey} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl ring-1 ring-green-200">
                      {product.picture_url && (
                        <img src={product.picture_url} alt={productName} className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="font-medium text-gray-900 truncate text-sm">{productName}</p>
                          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">
                            {t('manage.new', 'NEW')}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500">
                          {product.size && `${product.size} · `}
                          {product.quantity > 1 ? `${product.quantity}x ` : ''}{formatCurrency(product.unit_price * product.quantity)}
                        </p>
                      </div>
                      <button
                        onClick={() => { addonsModalShownRef.current = false; setAddedProducts(prev => prev.filter(p => !(p.product_type_id === product.product_type_id && (p.size || null) === (product.size || null)))); }}
                        className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-red-50 transition-colors"
                        aria-label={`${t('manage.remove', 'Remove')} ${productName}`}
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </m.div>

            <AddProductsSection
              booking={booking}
              locationId={booking.location_id}
              startDate={editedStartDate}
              endDate={editedEndDate}
              onAddProduct={handleAddProduct}
              onRemoveProduct={handleRemoveProduct}
              addedProducts={addedProducts}
              editedItems={editedItems}
              depositRules={depositRules}
              depositSwapBudget={depositSwapBudget}
              getDepositAmount={getDepositAmount}
            />

            <AddExtrasSection
              booking={booking}
              locationId={booking.location_id}
              startDate={editedStartDate}
              endDate={editedEndDate}
              existingAddonIds={editedAddons.map(a => a.product_type_id)}
              newAddons={newAddons}
              onAddAddon={handleAddStandaloneAddon}
              onRemoveAddon={handleRemoveStandaloneAddon}
              depositRules={merchant?.deposits_enabled ? (merchant?.deposit_rules || []) : null}
            />

            {editedAddons.length > 0 && (
              <m.div {...motion.fadeInUp} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <SafeIcon icon={FiPlus} className="w-5 h-5" style={{ color: theme.colors.primary.base }} />
                  {t('manage.addons', 'Add-ons')}
                </h2>
                <div className="space-y-3">
                  {editedAddons.map((addon) => {
                    const maxQty = getAddonMaxQuantity(addon);
                    const atMax = addon.quantity >= maxQty;
                    const addonName = getProductTranslation(
                      addon.item_name || addon.product_types?.product_name,
                      addon.product_types?.product_type_translations,
                      'product_name'
                    );
                    return (
                      <div key={addon.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 text-sm">{addonName}</p>
                          <p className="text-xs text-gray-500">{formatCurrency(addon.unit_price)}/{t('manage.each', 'ea')}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateAddonQuantity(addon.id, -1)}
                            className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 disabled:opacity-30"
                            disabled={addon.quantity <= 0}
                          >
                            <SafeIcon icon={FiMinus} className="w-4 h-4 text-gray-600" />
                          </button>
                          <span className="w-8 text-center font-semibold text-gray-900">{addon.quantity}</span>
                          <button
                            onClick={() => updateAddonQuantity(addon.id, 1)}
                            className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 disabled:opacity-30"
                            disabled={atMax}
                          >
                            <SafeIcon icon={FiPlus} className="w-4 h-4 text-gray-600" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </m.div>
            )}

            {getTotalBikesCount() === 0 && (
              <m.div {...motion.fadeInUp} role="status" aria-live="polite" className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
                {t('manage.cannotRemoveAllItems', 'A booking must keep at least one item. Add a replacement item or use Cancel Booking to remove the rental entirely.')}
              </m.div>
            )}

            <m.div {...motion.fadeInUp} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-gray-500">
                  <span>{t('manage.currentTotal', 'Current total')}</span>
                  <span>{formatCurrency(currentTotal)}</span>
                </div>
                <div className="flex justify-between text-gray-500">
                  <span>{t('manage.newTotal', 'New total')}</span>
                  <span>{formatCurrency(newTotal)}</span>
                </div>
                {priceDelta !== 0 && (
                  <div className={`flex justify-between font-semibold pt-2 border-t border-gray-200 ${priceDelta > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    <span>{priceDelta > 0 ? t('manage.additionalCharge', 'Additional charge') : t('manage.savings', 'Savings')}</span>
                    <span>{priceDelta > 0 ? `+${formatCurrency(priceDelta)}` : formatCurrency(Math.abs(priceDelta))}</span>
                  </div>
                )}
              </div>
            </m.div>

            <m.div {...motion.fadeInUp} className="flex gap-3 pb-8">
              <button
                onClick={() => navigate(`/b/${token}`)}
                className="flex-1 py-4 rounded-xl font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {t('manage.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleReview}
                className="flex-1 py-4 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                style={{ backgroundColor: theme.colors.primary.base }}
                disabled={!!priceAnchorError || getTotalBikesCount() === 0 || datesChanged && (checkingAvailability || !availabilityOk || !pricesRecalculated) || recalculatingPrices || (hasChanges() && maxFreeEdits !== null && !isFreeEdit && priceDelta <= 0)}
              >
                {t('manage.reviewChanges', 'Review Changes')}
                <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
              </button>
            </m.div>
          </>
        )}

        {step === 'review' && (
          <ReviewStep
            booking={booking}
            editedItems={editedItems}
            addedProducts={addedProducts}
            editedAddons={editedAddons}
            newAddons={newAddons}
            bundles={bundles}
            groupedOriginalItems={groupedOriginalItems}
            datesChanged={datesChanged}
            editedStartDate={editedStartDate}
            editedEndDate={editedEndDate}
            currentGroupQty={currentGroupQty}
            originalGroupQty={originalGroupQty}
            currentTotal={currentTotal}
            newTotal={newTotal}
            priceDelta={priceDelta}
            merchantTz={merchantTz}
            isUnpaid={isUnpaid}
            existingDiscount={existingDiscount}
            discountCode={booking.discount_code}
            saving={saving}
            onBack={() => { setStep('overview'); setError(null); }}
            onConfirm={handleConfirm}
          />
        )}

        {step === 'payment' && (
          <m.div {...motion.fadeInUp} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">{t('manage.payment', 'Payment')}</h2>
            <button
              onClick={() => { setStep('review'); setError(null); }}
              className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
              {t('manage.back', 'Back')}
            </button>
            <DeltaPaymentMethods
              token={token}
              merchant={merchant}
              booking={booking}
              amount={delta}
              description="Booking edit - additional charge"
              applyContext={{ changes: buildChangesPayload() }}
              savedCard={savedCard}
              idempotencyPrefix="edit"
              onPaid={(paymentIntentId, opts) => finalizeEdit(paymentIntentId, { changes: buildChangesPayload() }, opts)}
              onError={(msg) => setError(msg)}
            />
          </m.div>
        )}
      </main>

      <AddonsModal
        isOpen={showAddonsModal}
        onClose={() => { setShowAddonsModal(false); addonsModalShownRef.current = true; }}
        selectedProducts={addedProducts.filter(p => p.availableAddons?.length > 0).map(p => ({
          id: p.product_type_id,
          quantity: p.quantity,
          availableAddons: p.availableAddons
        }))}
        onContinue={handleAddonsConfirm}
        initialAddons={newAddons.length > 0 ? newAddons.map(a => ({ id: a.id, quantity: a.quantity })) : null}
        addonsData={addonsData}
        startDate={booking.start_date}
        flowType="manage"
      />

      <InsuranceWarningModal
        isOpen={isInsuranceWarningOpen}
        onClose={handleInsuranceDecline}
        onConfirm={handleInsuranceConfirm}
        bikeCount={getTotalBikesCount()}
        insuranceCount={getTotalInsuranceCount(pendingAddons)}
        addonsData={addonsData}
      />
    </div>
  );
}

function ReviewStep({
  booking,
  editedItems,
  addedProducts,
  editedAddons,
  newAddons,
  bundles,
  groupedOriginalItems,
  datesChanged,
  editedStartDate,
  editedEndDate,
  currentGroupQty,
  originalGroupQty,
  currentTotal,
  newTotal,
  priceDelta,
  merchantTz,
  isUnpaid,
  existingDiscount,
  discountCode,
  saving,
  onBack,
  onConfirm
}) {
  const theme = useTheme();
  const { formatCurrency } = useCurrency();
  const { getProductTranslation } = useLanguage();
  const { t } = useTranslation();
  const motion = useMotionConfig();

  return (
    <m.div {...motion.fadeInUp} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-4">
      <h2 className="text-lg font-semibold text-gray-900">{t('manage.reviewChanges', 'Review Changes')}</h2>

      <div className="space-y-2 text-sm">
        {datesChanged && (
          <>
            <div className="flex justify-between py-1">
              <span className="text-gray-600">{t('manage.pickUp', 'Pick-up')}</span>
              <span className="text-gray-900">{formatDisplayDate(editedStartDate, merchantTz)}</span>
            </div>
            <div className="flex justify-between py-1">
              <span className="text-gray-600">{t('manage.returnBy', 'Return by')}</span>
              <span className="text-gray-900">{formatDisplayDate(editedEndDate, merchantTz)}</span>
            </div>
          </>
        )}
        {bundles.map((bundle) => {
          const parentEdited = editedItems.find(i => i.id === bundle.parent.id);
          const isRemoved = !parentEdited || parentEdited.quantity === 0;
          const bundleName = getProductTranslation(
            bundle.parent.product_types?.product_name,
            bundle.parent.product_types?.product_type_translations,
            'product_name'
          );
          const parentQty = parentEdited?.quantity ?? bundle.parent.quantity ?? 1;
          const bundlePrice = (Number(bundle.parent.unit_price) || 0) * parentQty;
          return (
            <div key={`rev-bundle-${bundle.parent.id}`} className="flex justify-between py-1">
              <span className={`text-gray-600 ${isRemoved ? 'line-through' : ''}`}>{bundleName}</span>
              <span className="text-gray-900">
                {isRemoved ? t('manage.removed', 'Removed') : formatCurrency(bundlePrice)}
              </span>
            </div>
          );
        })}
        {groupedOriginalItems.map((group) => {
          const qty = currentGroupQty(group.groupKey);
          const origQty = originalGroupQty(group.groupKey);
          const qtyChanged = qty !== origQty;
          const productName = getProductTranslation(
            group.product_types?.product_name,
            group.product_types?.product_type_translations,
            'product_name'
          );
          const currentItem = editedItems.find(i =>
            `${i.product_type_id}_${i.size || ''}` === group.groupKey && i.quantity > 0
          );
          return (
            <div key={group.groupKey} className="flex justify-between py-1">
              <span className="text-gray-600">
                {productName}{group.size ? ` (${group.size})` : ''}
              </span>
              <div className="text-right">
                {qtyChanged ? (
                  <span className="text-gray-900">{origQty} &rarr; {qty}</span>
                ) : currentItem && qty > 0 ? (
                  <span className="text-gray-900">{formatCurrency(currentItem.unit_price * qty)}</span>
                ) : null}
                {datesChanged && currentItem && qty > 0 && (
                  <span className="block text-xs text-gray-500">
                    {formatCurrency(currentItem.unit_price)}/{t('manage.each', 'ea')}
                  </span>
                )}
              </div>
            </div>
          );
        })}
        {addedProducts.filter(p => p.quantity > 0).map(p => {
          const productName = getProductTranslation(
            p.product_name, p.translations, 'product_name'
          );
          return (
            <div key={`new-${p.product_type_id}_${p.size || ''}`} className="flex justify-between py-1">
              <span className="text-gray-600 flex items-center gap-1.5">
                {productName}{p.size ? ` (${p.size})` : ''}
                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">
                  {t('manage.new', 'NEW')}
                </span>
              </span>
              <div className="text-right">
                <span className="text-gray-900">{p.quantity}x</span>
                <span className="block text-xs text-gray-500">
                  {formatCurrency(p.unit_price)}/{t('manage.each', 'ea')}
                </span>
              </div>
            </div>
          );
        })}
        {editedAddons.filter(a => a.quantity !== a.originalQuantity).map(a => (
          <div key={a.id} className="flex justify-between py-1">
            <span className="text-gray-600">{getProductTranslation(a.item_name || a.product_types?.product_name, a.product_types?.product_type_translations, 'product_name')}</span>
            <span className="text-gray-900">{a.originalQuantity} &rarr; {a.quantity}</span>
          </div>
        ))}
        {editedAddons.filter(a => a.quantity === a.originalQuantity && a.quantity > 0).map(a => (
          <div key={a.id} className="flex justify-between py-1">
            <span className="text-gray-600">{getProductTranslation(a.item_name || a.product_types?.product_name, a.product_types?.product_type_translations, 'product_name')}</span>
            <span className="text-gray-900">{formatCurrency(a.unit_price * a.quantity)}</span>
          </div>
        ))}
        {newAddons.filter(a => a.quantity > 0).map(a => (
          <div key={`new-addon-${a.id}`} className="flex justify-between py-1">
            <span className="text-gray-600 flex items-center gap-1.5">
              {getProductTranslation(a.name || a.product_name, a.translations, 'product_name')}
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">
                {t('manage.new', 'NEW')}
              </span>
            </span>
            <div className="text-right">
              <span className="text-gray-900">{a.quantity}x</span>
              <span className="block text-xs text-gray-500">
                {formatCurrency(parseFloat(a.price))}/{t('manage.each', 'ea')}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="border-t border-gray-200 pt-3 space-y-2">
        {existingDiscount > 0 && (
          <div className="flex justify-between text-sm text-green-600">
            <span>{t('manage.discount', 'Discount')}{discountCode ? ` (${discountCode})` : ''}</span>
            <span>-{formatCurrency(existingDiscount)}</span>
          </div>
        )}
        <div className="flex justify-between text-sm text-gray-500">
          <span>{t('manage.currentTotal', 'Current total')}</span>
          <span>{formatCurrency(currentTotal)}</span>
        </div>
        <div className="flex justify-between font-bold text-lg text-gray-900">
          <span>{t('manage.newTotal', 'New total')}</span>
          <span>{formatCurrency(newTotal)}</span>
        </div>
        {priceDelta > 0 && !isUnpaid && (
          <div className="flex justify-between text-sm font-semibold text-red-600">
            <span>{t('manage.toPay', 'To pay')}</span>
            <span>+{formatCurrency(priceDelta)}</span>
          </div>
        )}
        {isUnpaid && (
          <div className="flex justify-between text-sm font-semibold text-red-600">
            <span>{t('manage.totalToPay', 'Total to pay')}</span>
            <span>{formatCurrency(newTotal)}</span>
          </div>
        )}
        {priceDelta < 0 && (
          <>
            <div className="flex justify-between text-sm font-semibold text-green-600">
              <span>{t('manage.difference', 'Difference')}</span>
              <span>{formatCurrency(priceDelta)}</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {t('manage.noAutoRefund', 'The new total is lower than what you\'ve paid. Refunds are not processed automatically. Please contact us to discuss a refund. Note that our cancellation policy may apply.')}
            </p>
          </>
        )}
      </div>

      <div className="flex gap-3 pt-2">
        <button
          onClick={onBack}
          className="flex-1 py-3 rounded-xl font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
        >
          {t('manage.back', 'Back')}
        </button>
        <button
          onClick={onConfirm}
          disabled={saving}
          className="flex-1 py-3 rounded-xl font-semibold text-white transition-colors disabled:opacity-50"
          style={{ backgroundColor: theme.colors.primary.base }}
        >
          {saving ? t('manage.saving', 'Saving...') : t('manage.confirmChanges', 'Confirm Changes')}
        </button>
      </div>
    </m.div>
  );
}
